import { createApiRef, DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';

export interface EnvironmentVersion {
  label: string;
  version: string | null;
}

export interface Product {
  productId: string;
  name: string;
  description: string;
  latestVersion: string;
  availableVersions: string[];
  owner: string;
  catalogEntity: string;
  sharedServices: { serviceId: string; version: string }[];
  environments: { production: EnvironmentVersion; preProduction: EnvironmentVersion | null };
}

export interface ClientDeployment {
  clientId: string;
  clientName: string;
  industry: string;
  region: string;
  deployments: {
    productId: string;
    productName: string;
    version: string;
    environment: string;
    environmentType: string;
    status: 'current' | 'outdated' | 'upgrading' | 'deprecated';
    lastUpdated: string;
  }[];
}

export interface SharedService {
  serviceId: string;
  name: string;
  latestVersion: string;
  catalogEntity: string;
  consumers: {
    productId: string;
    productName: string;
    serviceVersion: string;
  }[];
}

export interface VersionMatrix {
  products: { productId: string; name: string; latestVersion: string }[];
  clients: {
    clientId: string;
    clientName: string;
    deployments: {
      productId: string;
      version: string;
      environment: string;
      environmentType: string;
      status: string;
      isLatest: boolean;
    }[];
  }[];
}

export interface RolloutItem {
  customer: string;
  environment: string;
  environmentType: string;
  app: string;
  currentVersion: string;
  targetVersion: string;
  status: string;
  patchDateUtc: string | null;
  maintenanceWindow: { startHour: string; durationHours: string; timeZone: string } | null;
  email: {
    state: 'sent' | 'scheduled' | 'none' | 'missing' | 'other';
    status: string | null;
    notificationDate: string | null;
  };
  held: boolean;
  notNotified: boolean;
}

export interface RolloutResponse {
  asOf: string;
  items: RolloutItem[];
}

export interface ProductPortfolioApi {
  getProducts(): Promise<Product[]>;
  getClients(): Promise<ClientDeployment[]>;
  getClient(clientId: string): Promise<ClientDeployment>;
  getSharedServices(): Promise<SharedService[]>;
  getMatrix(): Promise<VersionMatrix>;
  getRollout(): Promise<RolloutResponse>;
}

export const productPortfolioApiRef = createApiRef<ProductPortfolioApi>({
  id: 'plugin.product-portfolio.api',
});

async function readJson<T>(response: Response): Promise<T> {
  if (!response.ok) {
    throw new Error(`Product portfolio request failed (${response.status})`);
  }
  return response.json();
}

export class ProductPortfolioClient implements ProductPortfolioApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  private async getBaseUrl(): Promise<string> {
    return await this.discoveryApi.getBaseUrl('product-portfolio');
  }

  async getProducts(): Promise<Product[]> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}/products`);
    return readJson(response);
  }

  async getClients(): Promise<ClientDeployment[]> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}/clients`);
    return readJson(response);
  }

  async getClient(clientId: string): Promise<ClientDeployment> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}/clients/${encodeURIComponent(clientId)}`);
    return readJson(response);
  }

  async getSharedServices(): Promise<SharedService[]> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}/shared-services`);
    return readJson(response);
  }

  async getMatrix(): Promise<VersionMatrix> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}/matrix`);
    return readJson(response);
  }

  async getRollout(): Promise<RolloutResponse> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}/qcore-rollout`);
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body.error ?? `Request failed with ${response.status}`);
    }
    return response.json();
  }
}
